/**
 * Module-level confirmDialog API. Replaces native `window.confirm()` /
 * `confirm()` calls with a styled, accessible promise-based modal.
 *
 * Usage:
 *   import { confirmDialog } from '@/lib/confirmDialog';
 *   if (await confirmDialog({ title: 'Delete?', message: 'Cannot be undone.', destructive: true })) {
 *     deleteIt();
 *   }
 *
 * Design follows the Kora admin template's modal/SweetAlert pattern:
 *   /Users/nielsvanharen/development/mijnrechtzaak/template/Kora-Main-File
 *   /Template/src/pages/components/SweetAlertPage.tsx
 *
 * Kora uses sweetalert2 for the actual rendering. We don't need a library
 * dependency for the handful of confirms in this app — the
 * `<ConfirmDialogHost />` component renders a plain glass modal styled to
 * match the rest of the Kora-dark theme. Same UX (title + message +
 * confirm/cancel + destructive variant) without the 80kB.
 */

export interface ConfirmDialogInput {
  /** Title shown in bold above the message. Optional. */
  title?: string;
  /** Body copy. Required. */
  message: string;
  /** Confirm button label. Defaults to translation `common.confirm`. */
  confirmLabel?: string;
  /** Cancel button label. Defaults to translation `common.cancel`. */
  cancelLabel?: string;
  /** Red confirm button + warning icon. Use for delete / destructive. */
  destructive?: boolean;
}

type Resolver = (confirmed: boolean) => void;

interface PendingDialog extends ConfirmDialogInput {
  resolve: Resolver;
}

type DialogHandler = (input: PendingDialog) => void;

let _showDialog: DialogHandler | null = null;

/** Called by <ConfirmDialogHost> on mount. Internal API. */
export function registerConfirmDialogHandler(fn: DialogHandler | null): void {
  _showDialog = fn;
}

export function confirmDialog(input: ConfirmDialogInput): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (!_showDialog) {
      // Host not mounted (early app boot, test environment, etc).
      // Fall through to native confirm so the call site keeps working.
      // Cleaner UX once the host mounts but never breaks.
      const ok =
        typeof window !== 'undefined' &&
        window.confirm(input.title ? `${input.title}\n\n${input.message}` : input.message);
      resolve(ok);
      return;
    }
    _showDialog({ ...input, resolve });
  });
}
